<template>
  <Dialog v-model:open="isOpen">
    <DialogContent
      class="report-preview-dialog-content !border-0 !bg-transparent !p-0 !shadow-none !max-w-none !w-auto flex items-center justify-center pointer-events-none z-50 transition-all duration-500 ease-out"
      @pointer-down-outside="(e) => { if (isRegenDialogOpen) e.preventDefault(); }"
      @interact-outside="(e) => { if (isRegenDialogOpen) e.preventDefault(); }"
    >
      <div
        class="flex h-[93vh] max-h-[95vh] items-center justify-center transition-all duration-500 ease-out pointer-events-auto"
        :class="isAiDrawerOpen ? 'w-[98vw] max-w-[1720px] gap-5' : 'w-[96vw] max-w-[1520px] gap-0'"
      >
        <!-- Left Floating Modal: Report Preview Modal -->
        <div
          class="flex h-full flex-col rounded-[1.4rem] border border-[#d8e4f0] bg-white shadow-[0_28px_80px_rgba(15,23,42,0.24)] overflow-hidden transition-all duration-500 ease-out"
          :class="isAiDrawerOpen ? 'w-[59%] shrink-0' : 'w-full'"
        >
          <!-- Header -->
          <div class="flex flex-col gap-3 border-b border-[#e3eaf4] px-6 py-3.5 sm:flex-row sm:items-center sm:justify-between bg-[#fcfdff]">
            <DialogHeader class="gap-y-0.5 text-left">
              <DialogTitle class="text-[1.3rem] font-extrabold text-[#0f172a] flex items-center gap-2.5">
                <span>Report Preview</span>
                <span
                  v-if="isAiDrawerOpen"
                  class="inline-flex items-center gap-1.5 rounded-full bg-[#eff6ff] px-2.5 py-0.5 text-xs font-bold text-[#2563eb] border border-[#bfdbfe]"
                >
                  <Sparkles class="h-3.5 w-3.5 animate-pulse text-[#2563eb]" />
                  AI Editor Active
                </span>
              </DialogTitle>
              <DialogDescription class="text-xs text-[#64748b]">
                {{ subtitle }}
              </DialogDescription>
            </DialogHeader>

            <button
              type="button"
              class="flex h-8 w-8 items-center justify-center rounded-xl text-[#64748b] hover:bg-[#f1f5f9] hover:text-[#0f172a] transition shrink-0"
              title="Close Preview"
              @click="closeDialog"
            >
              <X class="h-4 w-4" />
            </button>
          </div>

          <!-- Body Area: Report Preview Iframe -->
          <div class="flex-1 overflow-hidden bg-[#f8fbff] p-3 sm:p-4 flex min-h-0">
            <!-- Loading State while fetching HTML -->
            <div
              v-if="loading"
              class="flex h-full w-full min-h-[50vh] flex-col items-center justify-center rounded-[1.1rem] border border-[#d8e4f0] bg-white p-6 shadow-sm"
            >
              <div class="relative mb-4 flex h-16 w-16 items-center justify-center">
                <div class="absolute inset-0 animate-ping rounded-full bg-[#2563eb]/10" />
                <div class="absolute inset-1 animate-spin rounded-full border-4 border-[#e2e8f0] border-t-[#0f3557] [animation-duration:1.1s]" />
                <FileText class="h-6 w-6 text-[#2563eb] animate-pulse" />
              </div>
              <div class="text-sm font-extrabold text-[#0f3557]">
                Compiling Live Document...
              </div>
              <div class="mt-1 text-xs text-[#64748b]">
                Formatting metrics, charts, and report layout
              </div>
            </div>

            <!-- Error State -->
            <div
              v-else-if="error"
              class="flex h-full w-full min-h-[50vh] flex-col items-center justify-center rounded-[1.1rem] border border-dashed border-[#f87171] bg-white p-6 text-center"
            >
              <div class="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-[#fee2e2] text-[#dc2626]">
                <FileText class="h-6 w-6" />
              </div>
              <div class="text-sm font-bold text-[#b91c1c]">
                Failed to load live report
              </div>
              <div class="mt-1 max-w-sm text-xs text-[#64748b]">
                {{ error }}
              </div>
            </div>

            <!-- Active HTML Frame -->
            <div
              v-else
              class="report-preview-frame h-full w-full min-h-[50vh] overflow-hidden rounded-[1.1rem] border border-[#d8e4f0] bg-white shadow-[0_8px_24px_rgba(15,23,42,0.05)]"
            >
              <iframe
                ref="previewIframe"
                :srcdoc="srcDoc"
                class="h-full w-full bg-white transition-opacity duration-200"
                :class="{ 'pointer-events-none': isRegenDialogOpen }"
                sandbox="allow-same-origin allow-scripts allow-modals"
                title="Report preview"
                @load="handleIframeLoad"
              />
            </div>
          </div>

          <!-- Footer -->
          <DialogFooter class="border-t border-[#e3eaf4] px-6 py-3.5 bg-[#fcfdff]">
            <div class="flex w-full flex-col gap-2 sm:flex-row sm:items-center sm:justify-end">
              <!-- Delete Report Button in Footer -->
              <button
                v-if="report && canDelete"
                type="button"
                class="inline-flex items-center justify-center gap-2 rounded-[0.9rem] border border-[#fee2e2] bg-[#fff5f5] px-4 py-2 text-sm font-extrabold text-[#dc2626] transition hover:bg-[#fee2e2] hover:text-[#b91c1c]"
                @click="$emit('delete-report')"
              >
                <Trash2 class="h-4 w-4" />
                <span>Delete Report</span>
              </button>

              <button
                v-if="shareUrl"
                type="button"
                class="inline-flex items-center justify-center gap-1.5 rounded-[0.9rem] border border-[#bbf7d0] bg-[#f0fdf4] px-4 py-2 text-sm font-extrabold text-[#15803d] transition-all hover:bg-[#dcfce7] active:scale-[0.98]"
                title="Copy Shareable Link"
                @click="handleCopyShareLink"
              >
                <Link class="h-4 w-4" />
                <span>Copy Share Link</span>
              </button>

              <button
                v-if="canEdit"
                type="button"
                class="inline-flex items-center justify-center rounded-[0.9rem] border border-[#d7e2ef] bg-white px-4 py-2 text-sm font-extrabold text-[#334155] transition-colors hover:bg-[#f8fbff]"
                @click="$emit('edit-report')"
              >
                Edit Report
              </button>

              <button
                v-if="(report?.preview_html_url || report?.preview_url) && !isClient && canOpenFullPage"
                type="button"
                class="inline-flex items-center justify-center rounded-[0.9rem] border border-[#0f3557] bg-[#0f3557] px-4 py-2 text-sm font-extrabold text-white shadow-sm transition-all duration-200 hover:bg-[#1a4a6e] hover:shadow"
                @click="$emit('open-full-page')"
              >
                {{ openFullPageLabel }}
              </button>

              <button
                type="button"
                class="inline-flex items-center justify-center rounded-[0.9rem] border border-[#d7e2ef] bg-white px-4 py-2 text-sm font-extrabold text-[#334155] transition-colors hover:bg-[#f8fbff]"
                @click="closeDialog"
              >
                {{ closeLabel }}
              </button>
            </div>
          </DialogFooter>
        </div>

        <!-- Right Floating Modal: AI Section Editor Modal -->
        <transition
          enter-active-class="transition duration-400 ease-out"
          enter-from-class="opacity-0 translate-x-10 scale-95"
          enter-to-class="opacity-100 translate-x-0 scale-100"
          leave-active-class="transition duration-300 ease-in"
          leave-from-class="opacity-100 translate-x-0 scale-100"
          leave-to-class="opacity-0 translate-x-10 scale-95"
        >
          <div
            v-if="isAiDrawerOpen"
            class="w-[39%] shrink-0 h-full flex flex-col rounded-[1.4rem] border border-[#d8e4f0] bg-white shadow-[0_28px_80px_rgba(15,23,42,0.24)] overflow-hidden transition-all duration-500 ease-out"
          >
            <AiSectionEditorDrawer
              :open="isAiDrawerOpen"
              :report-id="report?.id || null"
              :client-id="client?.client_id || null"
              :section-key="activeAiSectionKey"
              :section-label="activeAiSectionLabel"
              :is-published="report?.status === 'published'"
              @update:open="isAiDrawerOpen = $event"
              @section-updated="handleSectionUpdated"
            />
          </div>
        </transition>
      </div>
    </DialogContent>
  </Dialog>

  <!-- Per-Section Document Upload & Regenerate Dialog -->
  <SectionRegenerateDialog
    :open="isRegenDialogOpen"
    :report-id="report?.id || null"
    :section-key="activeRegenSectionKey"
    :section-label="activeRegenSectionLabel"
    :is-auto-derived="activeRegenSectionKey === 'executiveSnapshot'"
    :current-ai-model="report?.report_data?.ai_model"
    @update:open="isRegenDialogOpen = $event"
    @regenerated="handleRegenerated"
  />
</template>

<script setup lang="ts">
import { computed, ref, onMounted, onUnmounted, nextTick } from "vue";
import { FileText, Link, Trash2, Sparkles, X } from "lucide-vue-next";
import { useToast } from "vue-toast-notification";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import type { ReportClientCard, ReportRecord } from "@/types/reports";
import { useReportLabels } from "@/composables/useReportLabels";
import AiSectionEditorDrawer from "@/components/reports/AiSectionEditorDrawer.vue";
import SectionRegenerateDialog from "@/components/reports/SectionRegenerateDialog.vue";

const toast = useToast();

const props = withDefaults(
  defineProps<{
    open: boolean;
    report: ReportRecord | null;
    client: ReportClientCard | null;
    loading?: boolean;
    error?: string | null;
    html?: string;
    role?: string | null;
    canEdit?: boolean;
    canAiEdit?: boolean;
    canOpenFullPage?: boolean;
    canDownload?: boolean;
    canDelete?: boolean;
    downloading?: boolean;
  }>(),
  {
    loading: false,
    error: null,
    html: "",
    role: null,
    canEdit: false,
    canAiEdit: true,
    canOpenFullPage: false,
    canDownload: true,
    canDelete: false,
    downloading: false,
  }
);

const emit = defineEmits<{
  (event: "update:open", value: boolean): void;
  (event: "edit-report"): void;
  (event: "open-full-page"): void;
  (event: "download-pdf"): void;
  (event: "delete-report"): void;
  (event: "refresh-report"): void;
}>();

const previewIframe = ref<HTMLIFrameElement | null>(null);

const isAiDrawerOpen = ref(false);
const activeAiSectionKey = ref("");
const activeAiSectionLabel = ref("");

const isRegenDialogOpen = ref(false);
const activeRegenSectionKey = ref("");
const activeRegenSectionLabel = ref("");

const isOpen = computed({
  get: () => props.open,
  set: (value: boolean) => emit("update:open", value),
});

const clientName = computed(() => props.client?.company_name || "Selected client");
const monthLabel = computed(() => props.report?.report_month_label || `Month ${props.report?.month_number || ""}`);
const subtitle = computed(() => {
  const parts = [clientName.value, monthLabel.value].filter(Boolean);
  return parts.join(" - ");
});
const { label, isClient } = useReportLabels(computed(() => props.role));
const canEdit = computed(() => Boolean(props.canEdit) && !isClient.value && !!props.report);
const canAiEditEffective = computed(() => Boolean(props.canAiEdit) && !isClient.value && !!props.report);
const canOpenFullPage = computed(
  () => Boolean(props.canOpenFullPage) && !!props.report
);
const openFullPageLabel = computed(() => label("openFullPage"));
const closeLabel = computed(() => (isClient.value ? label("backToReports") : "Close"));

const srcDoc = computed(() => {
  let html = props.html || "";
  if (!html.trim()) return "";

  const reportTitle = `${clientName.value} - ${monthLabel.value} Progress Report`;
  if (/<title>.*?<\/title>/i.test(html)) {
    html = html.replace(/<title>.*?<\/title>/i, `<title>${reportTitle}</title>`);
  }

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const baseTag = origin ? `<base href="${origin}/">` : "";

  // Inject styles & script for hover "Edit with AI" and "Regenerate" pills
  if (canAiEditEffective.value || canEdit.value) {
    const aiInjection = `
<style>
.dm-ai-edit-btn {
  position: absolute;
  top: 18px;
  right: 22px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  background: linear-gradient(135deg, #0f3557 0%, #2563eb 100%);
  color: #ffffff;
  font-family: 'Poppins', system-ui, -apple-system, sans-serif;
  font-size: 11.5px;
  font-weight: 700;
  border: 1px solid rgba(255,255,255,0.25);
  border-radius: 9999px;
  box-shadow: 0 4px 14px rgba(37,99,235,0.35);
  cursor: pointer;
  opacity: 0.9;
  transform: translateY(0);
  transition: all 0.25s cubic-bezier(0.16, 1, 0.3, 1);
  z-index: 999;
  user-select: none;
}
.dm-regen-btn {
  position: absolute;
  top: 18px;
  right: 142px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 13px;
  background: #ffffff;
  color: #1e3a5f;
  font-family: 'Poppins', system-ui, -apple-system, sans-serif;
  font-size: 11.5px;
  font-weight: 700;
  letter-spacing: 0.01em;
  border: 1px solid #bfdbfe;
  border-radius: 9999px;
  box-shadow: 0 3px 10px rgba(15,23,42,0.08);
  cursor: pointer;
  opacity: 0.92;
  transform: translateY(0);
  transition: all 0.25s cubic-bezier(0.16, 1, 0.3, 1);
  z-index: 999;
  user-select: none;
}
section.report-section {
  position: relative !important;
  transition: box-shadow 0.3s ease, outline 0.3s ease !important;
}
section.report-section:hover .dm-ai-edit-btn {
  opacity: 1;
  transform: translateY(-2px) scale(1.03);
  box-shadow: 0 6px 20px rgba(37,99,235,0.45);
  filter: brightness(1.1);
}
section.report-section:hover .dm-regen-btn {
  opacity: 1;
  transform: translateY(-2px) scale(1.02);
  box-shadow: 0 5px 16px rgba(37,99,235,0.22);
  border-color: #93c5fd;
}
.dm-regen-btn:hover {
  background: #f0f7ff !important;
  color: #1d4ed8 !important;
}
section.report-section.dm-ai-section-focus {
  outline: 3px solid #2563eb !important;
  outline-offset: 4px !important;
  box-shadow: 0 0 35px rgba(37, 99, 235, 0.2) !important;
}
.dm-ai-edit-btn svg {
  width: 14px;
  height: 14px;
}
.dm-regen-btn svg {
  width: 13px;
  height: 13px;
  color: #2563eb;
}
@media print {
  .dm-ai-edit-btn, .dm-regen-btn { display: none !important; }
}
</style>
<script>
(function() {
  const supportedSections = {
    'executive-snapshot': 'Executive Snapshot',
    'three-month-journey': 'The Three-Month Journey',
    'journey': 'The Three-Month Journey',
    'social-media-performance': 'Social Media Performance',
    'ai-chatbot': 'AI Chatbot & Automation',
    'ai-chatbot-management': 'AI Chatbot & Automation',
    'ai-podcast-progress': 'AI Podcast Progress',
    'ai-podcast': 'AI Podcast Progress',
    'podcast-management': 'AI Podcast Progress',
    'blog-management': 'Blog Management',
    'seo-management': 'SEO Management',
    'ads-management': 'Paid Ads Management',
    'generated-leads': 'Generated Leads',
    'promotional-materials': 'Promotional Materials',
    'monthly-recommendation': 'Monthly Recommendations'
  };

  const regenerableSections = {
    'executive-snapshot': 'Executive Snapshot',
    'social-media-performance': 'Social Media Performance',
    'ai-chatbot': 'AI Chatbot & Automation',
    'ai-chatbot-management': 'AI Chatbot & Automation',
    'ai-podcast-progress': 'AI Podcast Progress',
    'ai-podcast': 'AI Podcast Progress',
    'podcast-management': 'AI Podcast Progress',
    'blog-management': 'Blog Management',
    'seo-management': 'SEO Management',
    'ads-management': 'Paid Ads Management',
    'generated-leads': 'Generated Leads',
    'promotional-materials': 'Promotional Materials',
    'three-month-journey': 'The Three-Month Journey',
    'journey': 'The Three-Month Journey'
  };

  function injectButtons() {
    const allKeys = Array.from(new Set([...Object.keys(supportedSections), ...Object.keys(regenerableSections)]));
    allKeys.forEach(id => {
      const el = document.getElementById(id);
      if (!el) return;

      if (supportedSections[id] && !el.querySelector('.dm-ai-edit-btn')) {
        const btn = document.createElement('button');
        btn.className = 'dm-ai-edit-btn';
        btn.type = 'button';
        btn.title = 'Revise ' + supportedSections[id] + ' with AI';
        btn.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z"/></svg><span>Edit with AI</span>';
        btn.onclick = function(e) {
          e.preventDefault();
          e.stopPropagation();

          // Highlight and smooth scroll to section
          document.querySelectorAll('.dm-ai-section-focus').forEach(f => f.classList.remove('dm-ai-section-focus'));
          el.classList.add('dm-ai-section-focus');
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });

          window.parent.postMessage({
            type: 'OPEN_AI_SECTION_EDITOR',
            sectionId: id,
            sectionLabel: supportedSections[id]
          }, '*');
        };
        el.appendChild(btn);
      }

      if (regenerableSections[id] && !el.querySelector('.dm-regen-btn')) {
        const regenBtn = document.createElement('button');
        regenBtn.className = 'dm-regen-btn';
        regenBtn.type = 'button';
        regenBtn.title = id === 'executive-snapshot'
          ? 'Regenerate Executive Snapshot from active sections data'
          : 'Regenerate ' + regenerableSections[id] + ' by uploading a new source document';
        regenBtn.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/><path d="M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16"/><path d="M16 16h5v5"/></svg><span>Regenerate</span>';
        regenBtn.onclick = function(e) {
          e.preventDefault();
          e.stopPropagation();

          window.parent.postMessage({
            type: 'OPEN_REGEN_SECTION_MODAL',
            sectionId: id,
            sectionLabel: regenerableSections[id]
          }, '*');
        };
        el.appendChild(regenBtn);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', injectButtons);
  } else {
    injectButtons();
  }
})();
<\\/script>
`;
    if (/<\/body>/i.test(html)) {
      html = html.replace(/<\/body>/i, `${aiInjection}</body>`);
    } else {
      html += aiInjection;
    }
  }

  const hasHtmlRoot = /<html[\s>]/i.test(html);
  if (hasHtmlRoot) {
    if (/<head[\s>]/i.test(html)) {
      return html.replace(/<head([^>]*)>/i, `<head$1>${baseTag}`);
    }

    return html.replace(/<html([^>]*)>/i, `<html$1><head>${baseTag}</head>`);
  }

  return `<!doctype html><html><head>${baseTag}<title>${reportTitle}</title><meta charset="utf-8"></head><body>${html}</body></html>`;
});

const shareUrl = computed(() => props.report?.share_url || "");

async function handleCopyShareLink() {
  if (!shareUrl.value) return;
  let copied = false;
  try {
    if (navigator?.clipboard?.writeText && window.isSecureContext) {
      await navigator.clipboard.writeText(shareUrl.value);
      copied = true;
    }
  } catch {
    copied = false;
  }

  if (!copied) {
    try {
      const textarea = document.createElement("textarea");
      textarea.value = shareUrl.value;
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      textarea.style.left = "-9999px";
      textarea.style.top = "-9999px";
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      copied = document.execCommand("copy");
      document.body.removeChild(textarea);
    } catch {
      copied = false;
    }
  }

  if (copied) {
    toast.success("Shareable link copied to clipboard!");
  } else {
    toast.error("Please copy link manually: " + shareUrl.value);
  }
}

function handleMessageFromIframe(event: MessageEvent) {
  if (event.data?.type === "OPEN_AI_SECTION_EDITOR") {
    activeAiSectionKey.value = event.data.sectionId || "";
    activeAiSectionLabel.value = event.data.sectionLabel || "";
    isAiDrawerOpen.value = true;
  } else if (event.data?.type === "OPEN_REGEN_SECTION_MODAL") {
    const supported: Record<string, { camelKey: string; label: string }> = {
      "executive-snapshot": { camelKey: "executiveSnapshot", label: "Executive Snapshot" },
      "social-media-performance": { camelKey: "socialMediaPerformance", label: "Social Media Performance" },
      "ai-chatbot": { camelKey: "aiChatbotManagement", label: "AI Chatbot & Automation" },
      "ai-chatbot-management": { camelKey: "aiChatbotManagement", label: "AI Chatbot & Automation" },
      "ai-podcast-progress": { camelKey: "aiPodcastProgress", label: "AI Podcast Progress" },
      "podcast-management": { camelKey: "aiPodcastProgress", label: "AI Podcast Progress" },
      "blog-management": { camelKey: "blogManagement", label: "Blog Management" },
      "seo-management": { camelKey: "seoManagement", label: "SEO Management" },
      "ads-management": { camelKey: "adsManagement", label: "Paid Ads Management" },
      "generated-leads": { camelKey: "generatedLeads", label: "Generated Leads" },
      "promotional-materials": { camelKey: "promotionalMaterials", label: "Promotional Materials" },
      "three-month-journey": { camelKey: "journey", label: "The Three-Month Journey" },
      "journey": { camelKey: "journey", label: "The Three-Month Journey" },
    };
    const cfg = supported[event.data.sectionId] || { camelKey: event.data.sectionId, label: event.data.sectionLabel };
    activeRegenSectionKey.value = cfg.camelKey;
    activeRegenSectionLabel.value = cfg.label;
    isRegenDialogOpen.value = true;
  }
}

function handleIframeLoad() {
  if ((!canAiEditEffective.value && !canEdit.value) || !previewIframe.value) return;
  try {
    const doc = previewIframe.value.contentDocument || previewIframe.value.contentWindow?.document;
    if (!doc) return;

    const supportedSections: Record<string, string> = {
      "executive-snapshot": "Executive Snapshot",
      "three-month-journey": "The Three-Month Journey",
      "journey": "The Three-Month Journey",
      "social-media-performance": "Social Media Performance",
      "ai-chatbot": "AI Chatbot & Automation",
      "ai-chatbot-management": "AI Chatbot & Automation",
      "ai-podcast-progress": "AI Podcast Progress",
      "ai-podcast": "AI Podcast Progress",
      "podcast-management": "AI Podcast Progress",
      "blog-management": "Blog Management",
      "seo-management": "SEO Management",
      "ads-management": "Paid Ads Management",
      "generated-leads": "Generated Leads",
      "promotional-materials": "Promotional Materials",
      "monthly-recommendation": "Monthly Recommendations",
    };

    const regenerableSections: Record<string, { camelKey: string; label: string }> = {
      "executive-snapshot": { camelKey: "executiveSnapshot", label: "Executive Snapshot" },
      "social-media-performance": { camelKey: "socialMediaPerformance", label: "Social Media Performance" },
      "ai-chatbot": { camelKey: "aiChatbotManagement", label: "AI Chatbot & Automation" },
      "ai-chatbot-management": { camelKey: "aiChatbotManagement", label: "AI Chatbot & Automation" },
      "ai-podcast-progress": { camelKey: "aiPodcastProgress", label: "AI Podcast Progress" },
      "ai-podcast": { camelKey: "aiPodcastProgress", label: "AI Podcast Progress" },
      "podcast-management": { camelKey: "aiPodcastProgress", label: "AI Podcast Progress" },
      "blog-management": { camelKey: "blogManagement", label: "Blog Management" },
      "seo-management": { camelKey: "seoManagement", label: "SEO Management" },
      "ads-management": { camelKey: "adsManagement", label: "Paid Ads Management" },
      "generated-leads": { camelKey: "generatedLeads", label: "Generated Leads" },
      "promotional-materials": { camelKey: "promotionalMaterials", label: "Promotional Materials" },
      "three-month-journey": { camelKey: "journey", label: "The Three-Month Journey" },
      "journey": { camelKey: "journey", label: "The Three-Month Journey" },
    };

    if (!doc.getElementById("dm-ai-edit-styles")) {
      const style = doc.createElement("style");
      style.id = "dm-ai-edit-styles";
      style.textContent = `
        .dm-ai-edit-btn {
          position: absolute;
          top: 18px;
          right: 22px;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 6px 14px;
          background: linear-gradient(135deg, #0f3557 0%, #2563eb 100%);
          color: #ffffff;
          font-family: 'Poppins', system-ui, -apple-system, sans-serif;
          font-size: 11.5px;
          font-weight: 700;
          letter-spacing: 0.01em;
          border: 1px solid rgba(255,255,255,0.25);
          border-radius: 9999px;
          box-shadow: 0 4px 14px rgba(37,99,235,0.35);
          cursor: pointer;
          opacity: 0.9;
          transform: translateY(0);
          transition: all 0.25s cubic-bezier(0.16, 1, 0.3, 1);
          z-index: 999;
          user-select: none;
        }
        .dm-regen-btn {
          position: absolute;
          top: 18px;
          right: 142px;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 6px 13px;
          background: #ffffff;
          color: #1e3a5f;
          font-family: 'Poppins', system-ui, -apple-system, sans-serif;
          font-size: 11.5px;
          font-weight: 700;
          letter-spacing: 0.01em;
          border: 1px solid #bfdbfe;
          border-radius: 9999px;
          box-shadow: 0 3px 10px rgba(15,23,42,0.08);
          cursor: pointer;
          opacity: 0.92;
          transform: translateY(0);
          transition: all 0.25s cubic-bezier(0.16, 1, 0.3, 1);
          z-index: 999;
          user-select: none;
        }
        section.report-section {
          position: relative !important;
          transition: box-shadow 0.3s ease, outline 0.3s ease !important;
        }
        section.report-section:hover .dm-ai-edit-btn {
          opacity: 1;
          transform: translateY(-2px) scale(1.03);
          box-shadow: 0 6px 20px rgba(37,99,235,0.45);
          filter: brightness(1.1);
        }
        section.report-section:hover .dm-regen-btn {
          opacity: 1;
          transform: translateY(-2px) scale(1.02);
          box-shadow: 0 5px 16px rgba(37,99,235,0.22);
          border-color: #93c5fd;
        }
        .dm-regen-btn:hover {
          background: #f0f7ff !important;
          color: #1d4ed8 !important;
        }
        section.report-section.dm-ai-section-focus {
          outline: 3px solid #2563eb !important;
          outline-offset: 4px !important;
          box-shadow: 0 0 35px rgba(37, 99, 235, 0.2) !important;
        }
        .dm-ai-edit-btn svg {
          width: 14px;
          height: 14px;
        }
        .dm-regen-btn svg {
          width: 13px;
          height: 13px;
          color: #2563eb;
        }
        @media print {
          .dm-ai-edit-btn, .dm-regen-btn { display: none !important; }
        }
      `;
      doc.head?.appendChild(style);
    }

    const allSectionKeys = Array.from(new Set([...Object.keys(supportedSections), ...Object.keys(regenerableSections)]));
    allSectionKeys.forEach((id) => {
      const el = doc.getElementById(id);
      if (!el) return;

      // 1. Inject Edit with AI button
      if (canAiEditEffective.value && supportedSections[id] && !el.querySelector(".dm-ai-edit-btn")) {
        const btn = doc.createElement("button");
        btn.className = "dm-ai-edit-btn";
        btn.type = "button";
        btn.title = `Revise ${supportedSections[id]} with AI`;
        btn.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z"/></svg><span>Edit with AI</span>`;
        btn.addEventListener("click", (e) => {
          e.preventDefault();
          e.stopPropagation();

          // Highlight and scroll to center
          doc.querySelectorAll(".dm-ai-section-focus").forEach((f) => f.classList.remove("dm-ai-section-focus"));
          el.classList.add("dm-ai-section-focus");
          el.scrollIntoView({ behavior: "smooth", block: "center" });

          activeAiSectionKey.value = id;
          activeAiSectionLabel.value = supportedSections[id];
          isAiDrawerOpen.value = true;
        });
        el.appendChild(btn);
      }

      // 2. Inject Regenerate button (for sections that accept document uploads)
      const regenConfig = regenerableSections[id];
      if (regenConfig && !el.querySelector(".dm-regen-btn") && (canEdit.value || canAiEditEffective.value)) {
        const regenBtn = doc.createElement("button");
        regenBtn.className = "dm-regen-btn";
        regenBtn.type = "button";
        regenBtn.title = id === 'executive-snapshot'
          ? 'Regenerate Executive Snapshot from active sections data'
          : `Regenerate ${regenConfig.label} by uploading a new source document`;
        regenBtn.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/><path d="M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16"/><path d="M16 16h5v5"/></svg><span>Regenerate</span>`;
        regenBtn.addEventListener("click", (e) => {
          e.preventDefault();
          e.stopPropagation();

          activeRegenSectionKey.value = regenConfig.camelKey;
          activeRegenSectionLabel.value = regenConfig.label;
          isRegenDialogOpen.value = true;
        });
        el.appendChild(regenBtn);
      }
    });
  } catch (err) {
    console.error("Failed injecting section action buttons into preview frame:", err);
  }
}

function handleRegenerated(updatedReport: ReportRecord) {
  emit("refresh-report");
}

function handleSectionUpdated(sectionKey: string) {
  emit("refresh-report");
}

function closeDialog() {
  isAiDrawerOpen.value = false;
  emit("update:open", false);
}

onMounted(() => {
  window.addEventListener("message", handleMessageFromIframe);
});

onUnmounted(() => {
  window.removeEventListener("message", handleMessageFromIframe);
});
</script>

<style>
.report-preview-dialog-content > button.absolute,
.report-preview-dialog-content > button:has(.sr-only) {
  display: none !important;
  visibility: hidden !important;
  pointer-events: none !important;
}
</style>

<style scoped>
:deep([data-radix-dialog-content] > button.absolute),
:deep([data-slot="dialog-content"] > button.absolute) {
  display: none !important;
}

:deep(iframe) {
  border: 0;
}
</style>
